import React from 'react';

/**
 * Return a rotating `size`-chip window over `pool`, wrapping around so each
 * ↻ press shows a fresh trio (and eventually cycles back to the start).
 */
export function windowPrompts(pool: string[], page: number, size = 4): string[] {
  if (pool.length === 0) return [];
  const start = (page * size) % pool.length;
  const out: string[] = [];
  for (let i = 0; i < Math.min(size, pool.length); i++) {
    out.push(pool[(start + i) % pool.length]);
  }
  return out;
}

interface QuickPromptBarProps {
  /** Agent-tailored suggestions (shown on page 0 when present). */
  agentPrompts?: string[] | null;
  /** Static fallback pool, rotated through by the ↻ button. */
  staticPool: string[];
  /** Current rotation page (owned by the parent). */
  page: number;
  /** Advance to the next rotation page. */
  onCycle: () => void;
  /** Send a chosen prompt. */
  onSend: (prompt: string) => void;
  disabled?: boolean;
}

/**
 * The quick-prompt chip row shared by every agent chat pane (knowledge,
 * structure, page-builder, discovery). Page 0 shows the agent's tailored
 * suggestions when present; ↻ swaps in a fresh trio from the static pool.
 */
export function QuickPromptBar({
  agentPrompts,
  staticPool,
  page,
  onCycle,
  onSend,
  disabled = false,
}: QuickPromptBarProps) {
  const agent = (agentPrompts ?? []).filter((p) => p && p.trim());
  const hasAgent = agent.length > 0;
  const showingAgent = hasAgent && page === 0;
  const quickPrompts = showingAgent
    ? agent
    : windowPrompts(staticPool, hasAgent ? page - 1 : page);
  const canCycle = hasAgent || staticPool.length > 4;

  if (quickPrompts.length === 0) return null;

  return (
    <div className="mb-2">
      <div className="flex items-center gap-1.5 mb-1">
        <span className="text-[10px] uppercase tracking-wide text-gray-400 dark:text-gray-500">
          {showingAgent ? 'Suggested next steps' : 'Quick prompts'}
        </span>
        {canCycle && (
          <button
            type="button"
            onClick={onCycle}
            className="text-[11px] text-gray-400 dark:text-gray-500 hover:text-indigo-600 dark:hover:text-indigo-400 leading-none"
            title="Show a fresh set of ideas"
          >
            ↻
          </button>
        )}
      </div>
      {/* NOT PILLS. `rounded-full` is a shape for one or two words; the agent's
          suggestions are whole sentences ("Endorse the keywords that are strictly
          about Age & Life Stage"), and a sentence wrapped inside a capsule reads as a
          lozenge with the text floating off its curves. Square-ish corners, a real
          line-height and a two-line cap give a long suggestion the shape of something
          you click and a short one the shape of a chip.

          `max-w-full` matters more than it looks: without it a long suggestion sets a
          flex item wider than the pane and the whole chip row scrolls sideways. */}
      <div className="flex flex-wrap gap-1.5">
        {quickPrompts.map((p, i) => (
          <button
            key={`${i}-${p}`}
            type="button"
            onClick={() => onSend(p)}
            disabled={disabled}
            title={`Send: "${p}"`}
            className={`max-w-full px-2.5 py-1.5 text-xs leading-snug rounded-md border text-left transition disabled:opacity-50 ${
              showingAgent
                ? 'border-indigo-200 dark:border-indigo-800 bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 hover:bg-indigo-100 dark:hover:bg-indigo-900/40'
                : 'border-sky-200 dark:border-sky-800 bg-sky-50 dark:bg-sky-900/30 text-sky-700 dark:text-sky-300 hover:bg-sky-100 dark:hover:bg-sky-900/40'
            }`}
          >
            <span className="block line-clamp-2">{p}</span>
          </button>
        ))}
      </div>
    </div>
  );
}
